import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { db } from "../supabase";
import { useAngemeldet, useSitzung } from "../sitzung";
import { fehlertext } from "../fehler";
import { Avatar } from "../ui/Avatar";
import { Feld } from "../ui/Feld";
import { FotoKnopf, useFoto } from "../ui/FotoKnopf";
import { Karte } from "../ui/Karte";
import { Knopf } from "../ui/Knopf";
import { Meldung } from "../ui/Leer";
import { PushEinrichten } from "./PushEinrichten";
import s from "./seiten.module.css";

const SCHRITTE = ["Name", "Foto", "Push"] as const;

/** Ansicht 19 (Erste Anmeldung) in drei Schritten (BAUPLAN-PAKETE.md, Abschnitt 4.5): Name und „So funktioniert es“, Foto, Push.
 *  Foto und Push lassen sich auf „Später“ schieben, die Karte „Erste Schritte“ auf Meine Woche erinnert daran. Erst am Ende gilt die
 *  erste Anmeldung als fertig, vorher führt die App wieder hierher. */
export function Willkommen() {
  const sitzung = useAngemeldet();
  const { neuLaden } = useSitzung();
  const navigate = useNavigate();
  const foto = useFoto();
  const [schritt, setSchritt] = useState(0);
  const [vorname, setVorname] = useState(sitzung.person.vorname);
  const [name, setName] = useState(sitzung.person.name);
  const [aufgabe, setAufgabe] = useState(sitzung.person.aufgabe);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const ueberschrift = useRef<HTMLHeadingElement>(null);

  // Nach jedem Schritt: Fokus auf die Überschrift (Tastatur und Screenreader)
  useEffect(() => {
    if (schritt > 0) ueberschrift.current?.focus();
  }, [schritt]);

  if (sitzung.person.erste_anmeldung_am) return <Navigate to="/" replace />;

  async function nameSpeichern(e: FormEvent) {
    e.preventDefault();
    if (!vorname.trim()) {
      setFehler("Bitte trag deinen Vornamen ein.");
      return;
    }
    setLaeuft(true);
    setFehler(null);
    const { error } = await db().from("app_personen").update({ vorname: vorname.trim(), name: name.trim(), aufgabe: aufgabe.trim() }).eq("id", sitzung.person.id);
    setLaeuft(false);
    if (error) {
      setFehler(fehlertext(error));
      return;
    }
    setSchritt(1);
  }

  async function fertig() {
    setLaeuft(true);
    setFehler(null);
    const { error } = await db().rpc("app_erste_anmeldung_fertig");
    setLaeuft(false);
    if (error) {
      setFehler(fehlertext(error));
      return;
    }
    await neuLaden();
    navigate("/", { replace: true });
  }

  const weiter = (
    <div className={s.zeile}>
      {schritt === 1 ? (
        <>
          <Knopf art="leise" onClick={() => setSchritt(2)} disabled={laeuft}>
            Später
          </Knopf>
          <Knopf art="primaer" onClick={() => setSchritt(2)} disabled={laeuft || foto.laeuft}>
            Weiter
          </Knopf>
        </>
      ) : (
        <>
          <Knopf art="leise" onClick={() => void fertig()} disabled={laeuft}>
            Später
          </Knopf>
          <Knopf art="primaer" onClick={() => void fertig()} disabled={laeuft}>
            {laeuft ? "Einen Moment …" : "Zu Meine Woche"}
          </Knopf>
        </>
      )}
    </div>
  );

  return (
    <div className={s.stapel} style={{ maxWidth: 720 }}>
      <div className={s.kopf}>
        <div>
          <h1 ref={ueberschrift} tabIndex={-1}>
            {schritt === 0 ? `Willkommen, ${sitzung.person.vorname || "schön, dass du da bist"}.` : schritt === 1 ? "Ein Foto von dir?" : "Push aufs Handy?"}
          </h1>
          <p>
            Schritt {schritt + 1} von {SCHRITTE.length}: {SCHRITTE[schritt]}
          </p>
        </div>
      </div>

      {schritt === 0 ? (
        <form onSubmit={nameSpeichern} className={s.stapel}>
          <Karte titel="Stimmt dein Name?" unterzeile="So sehen dich die anderen in der App.">
            <div className={s.zweiSpalten}>
              <Feld label="Vorname" value={vorname} onChange={(e) => setVorname(e.target.value)} autoComplete="given-name" required maxLength={80} />
              <Feld label="Nachname" value={name} onChange={(e) => setName(e.target.value)} autoComplete="family-name" maxLength={80} />
            </div>
            <Feld label="Aufgabe im Verein" hilfe="Zum Beispiel Kasse, Kommunikation oder Technik. Freiwillig." value={aufgabe} onChange={(e) => setAufgabe(e.target.value)} maxLength={120} />
          </Karte>
          <Karte titel="So funktioniert es">
            <ul className={s.stapel} style={{ margin: 0, paddingLeft: 20 }}>
              <li>Unter „Meine Woche“ siehst du, was bei dir liegt, sortiert nach dem, was zuerst dran ist. Ein Haken vor der Aufgabe erledigt sie.</li>
              <li>Unter „Offen“ stehen Aufgaben, für die noch jemand gesucht wird. Ein Klick, und sie liegt bei dir.</li>
              <li>Gearbeitet wird in Sprints von zwei Wochen. Ein Sprint heißt nach dem Tag, an dem er endet, zum Beispiel „Sprint 26.10.“.</li>
            </ul>
          </Karte>
          {fehler ? <Meldung fehler>{fehler}</Meldung> : null}
          <div>
            <Knopf art="primaer" type="submit" disabled={laeuft}>
              {laeuft ? "Wird gespeichert …" : "Weiter"}
            </Knopf>
          </div>
        </form>
      ) : null}

      {schritt === 1 ? (
        <>
          <Karte titel="Foto" unterzeile="Damit dich die anderen in Listen und auf dem Board erkennen. Freiwillig, nur angemeldete Personen sehen es.">
            <div className={s.foto}>
              <Avatar person={sitzung.person} groesse={96} />
              <div className={s.zeile}>
                <FotoKnopf foto={foto} art={foto.hatFoto ? "normal" : "primaer"} />
              </div>
            </div>
          </Karte>
          {fehler ? <Meldung fehler>{fehler}</Meldung> : null}
          {weiter}
        </>
      ) : null}

      {schritt === 2 ? (
        <>
          <p>Wir sagen dir Bescheid, wenn dir jemand eine Aufgabe gibt oder dich erwähnt. Was genau, stellst du unter „Mitteilungen“ ein.</p>
          <PushEinrichten />
          {fehler ? <Meldung fehler>{fehler}</Meldung> : null}
          {weiter}
        </>
      ) : null}

      <p className={s.klein}>
        Welche Daten die App verarbeitet und wer sie sieht, steht im <Link to="/datenschutz">Datenschutzhinweis</Link>.
      </p>
    </div>
  );
}
